import { reactive, computed, watch, set as vueSet } from '@vue/composition-api'
import axios from '@axios'
import { getDatasetDefaultFrom, getDatasetModule } from '../config/datasets'
import { resolveTimeBound } from '../utils/timeUtils'

/**
 * Maps raw API response items to { value, label } using the mapItem config.
 */
function applyMapItem(items, mapItem) {
  if (typeof mapItem === 'function') {
    return items.map(mapItem)
  }
  return items.map(item => ({
    value: item[mapItem.value],
    label: item[mapItem.label],
  }))
}

/**
 * Composable for loading filter options.
 *
 * Reads each filter's optionsConfig directly from the dataset module,
 * supports types: 'static', 'api', 'embed', 'date', 'freetext'.
 *
 * @param {import('@vue/composition-api').Ref<string>} selectedDataset
 * @param {import('@vue/composition-api').ComputedRef<string[]>} filterKeys
 * @param {{ baseUrl?: import('@vue/composition-api').Ref<string>, token?: import('@vue/composition-api').Ref<string> }} options
 */
export function useChartFilterOptions(selectedDataset, filterKeys, { baseUrl, token, activeFilters, activeProcessFormLabel, setFilter } = {}) {
  const filterOptionsMap = reactive({})
  const fetchedEndpoints = {}

  /**
   * If a filter's current selection is no longer present in its options
   * (because the cascading where-clause narrowed it out), clear the orphan
   * parts. Supports comma-separated multi-value selections.
   */
  const pruneOrphanSelection = (key, options) => {
    if (!setFilter) return
    const current = activeFilters?.value?.[key]
    if (current === null || current === undefined || current === '') return
    const validValues = new Set((options || []).map(o => String(o?.value)))
    const parts = String(current).split(',').filter(Boolean)
    const kept = parts.filter(p => validValues.has(p))
    if (kept.length === parts.length) return
    setFilter(key, kept.length > 0 ? kept.join(',') : null)
  }

  /**
   * Get the optionsConfig for a filter key from the dataset module.
   */
  const getFilterOptionsConfig = (dataset, filterKey) => {
    const module = getDatasetModule(dataset)
    if (!module) return { type: 'freetext' }
    let filter
    if (filterKey === 'question' && activeProcessFormLabel?.value) {
      filter = module.filters.find(f => f.key === filterKey && (f.forField || f.label) === activeProcessFormLabel.value)
    } else {
      filter = module.filters.find(f => f.key === filterKey)
    }
    return filter?.optionsConfig || { type: 'freetext' }
  }

  /**
   * Build a JSON `where` string from all active where/where-date filters,
   * excluding the given key so each filter's options reflect the state of
   * the OTHER filters (cascading behavior). Mirrors useChartUrl.js logic.
   */
  const buildCascadeWhere = (dataset, excludeKey) => {
    const module = getDatasetModule(dataset)
    if (!module) return ''
    const filters = activeFilters?.value || {}
    const whereClause = {}
    Object.keys(filters).forEach(filterKey => {
      if (filterKey === excludeKey) return
      const val = filters[filterKey]
      if (val === null || val === undefined || val === '') return
      const moduleFilter = module.filters.find(f => f.key === filterKey)
      if (!moduleFilter) return
      const column = moduleFilter.whereColumn || filterKey
      if (moduleFilter.filterType === 'where') {
        const values = String(val).split(',').filter(Boolean)
        if (values.length > 0) whereClause[column] = { operator: 'in', values }
      } else if (moduleFilter.filterType === 'where-date') {
        const rawVal = String(val)
        if (rawVal.includes(' to ')) {
          const [start, end] = rawVal.split(' to ')
          whereClause[column] = { operator: 'between', values: [`${start} 00:00:00`, `${end} 23:59:59`] }
        } else {
          whereClause[column] = { operator: 'like', values: [`${rawVal}%`] }
        }
      }
    })
    return Object.keys(whereClause).length > 0 ? JSON.stringify(whereClause) : ''
  }

  const loadFilterOptions = async ({ preserveOptions = false } = {}) => {
    const dataset = selectedDataset.value
    if (!dataset) return

    const keys = filterKeys.value || []
    const newMap = {}

    for (const key of keys) {
      const config = getFilterOptionsConfig(dataset, key)
      // Keep prior options visible while refetching on filter-value changes,
      // so dropdowns don't flash empty during cascading reloads.
      const prevOptions = preserveOptions ? (filterOptionsMap[key]?.options || []) : []

      if (config.type === 'static') {
        const opts = (config.options || []).map(o =>
          o.label !== undefined ? o : { ...o, label: o.value }
        )
        newMap[key] = {
          type: 'static',
          options: opts,
          loading: false,
          valueField: config.valueField || 'value',
        }
      } else if (config.type === 'api' || config.type === 'embed' || config.type === 'user-embed') {
        newMap[key] = {
          type: config.type,
          options: prevOptions,
          loading: true,
          valueField: config.valueField || 'value',
        }
      } else if (config.type === 'json-keys') {
        newMap[key] = {
          type: 'json-keys',
          options: preserveOptions ? (filterOptionsMap[key]?.options || []) : [],
          questionsMap: preserveOptions ? (filterOptionsMap[key]?.questionsMap || {}) : {},
          loading: true,
          valueField: 'value',
        }
      } else {
        newMap[key] = {
          type: config.type,
          options: prevOptions,
          loading: false,
          valueField: 'value',
        }
      }
    }

    // Force Vue 2 reactivity: clear and reassign all keys
    Object.keys(filterOptionsMap).forEach(k => delete filterOptionsMap[k])
    Object.keys(newMap).forEach(k => {
      vueSet(filterOptionsMap, k, newMap[k])
    })

    // Fetch options for 'api' and 'embed' type filters
    for (const key of keys) {
      const config = getFilterOptionsConfig(dataset, key)

      if (config.type === 'api') {
        try {
          let items
          if (fetchedEndpoints[config.endpoint]) {
            items = fetchedEndpoints[config.endpoint]
          } else {
            const { data } = await axios.get(config.endpoint)
            items = data.data || data || []
            fetchedEndpoints[config.endpoint] = items
          }
          const options = applyMapItem(items, config.mapItem)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], options, loading: false })
          }
          pruneOrphanSelection(key, options)
        } catch (e) {
          console.warn(`[ChartFilterOptions] Failed to fetch ${config.endpoint}:`, e)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], loading: false })
          }
        }
      } else if (config.type === 'embed') {
        try {
          const base = baseUrl?.value || ''
          const tk = token?.value || ''
          const params = new URLSearchParams()
          params.set('embed', 'true')
          params.set('theme', 'dark')
          params.set('dataset', config.dataset)
          params.set('view', 'json')
          params.set('columns', config.by)
          if (!config.by.includes(',')) {
            const distinctCol = `DISTINCT(${config.by})`
            params.set('mapping', JSON.stringify({
              _columns_: { [distinctCol]: 'filter' }
            }))
          }
          if (!getDatasetModule(config.dataset)?.omitTimeRange) {
            params.set('from', resolveTimeBound(activeFilters?.value?.from || config.from || getDatasetDefaultFrom(config.dataset)))
            const currentTo = activeFilters?.value?.to
            if (currentTo) params.set('to', resolveTimeBound(currentTo, { end: true }))
          }
          params.set('distinct', 'true')
          params.set('limit', '-1')
          if (tk) params.set('token', tk)
          params.set('plaintext', 'true')

          // Cascading filters: narrow this filter's options by the OTHER active filters.
          const cascadeWhere = buildCascadeWhere(dataset, key)
          if (cascadeWhere) params.set('where', cascadeWhere)

          const url = `${base}/?${params.toString().replace(/%2C/gi, ',')}`
          const cacheKey = `embed:${url}`

          let items
          if (fetchedEndpoints[cacheKey]) {
            items = fetchedEndpoints[cacheKey]
          } else {
            const res = await fetch(url)
            const data = await res.json()
            const raw = Array.isArray(data) ? data : []
            // Deduplicate and filter out nulls/empties
            // Supports both mapped ('filter' key) and legacy (first value) formats
            const seen = new Set()
            items = raw.filter(item => {
              const v = item.filter !== undefined ? item.filter : Object.values(item)[0]
              if (v == null || v === '' || seen.has(v)) return false
              seen.add(v)
              return true
            })
            fetchedEndpoints[cacheKey] = items
          }

          const options = applyMapItem(items, config.mapItem)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], options, loading: false })
          }
          pruneOrphanSelection(key, options)
        } catch (e) {
          console.warn(`[ChartFilterOptions] Failed to fetch embed options for ${key}:`, e)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], loading: false })
          }
        }
      } else if (config.type === 'user-embed') {
        try {
          const base = baseUrl?.value || ''
          const tk = token?.value || ''
          const USER_COLS = ['users.id', 'users.user', 'users.name', 'users.first_name', 'users.second_name']

          const params = new URLSearchParams()
          params.set('embed', 'true')
          params.set('theme', 'dark')
          params.set('dataset', config.dataset)
          params.set('view', 'json')
          params.set('columns', USER_COLS.join(','))
          if (!getDatasetModule(config.dataset)?.omitTimeRange) {
            params.set('from', resolveTimeBound(activeFilters?.value?.from || config.from || getDatasetDefaultFrom(config.dataset)))
            const currentTo = activeFilters?.value?.to
            if (currentTo) params.set('to', resolveTimeBound(currentTo, { end: true }))
          }
          params.set('distinct', 'true')
          params.set('limit', '-1')
          if (tk) params.set('token', tk)
          params.set('plaintext', 'true')

          const cascadeWhere = buildCascadeWhere(dataset, key)
          if (cascadeWhere) params.set('where', cascadeWhere)

          const url = `${base}/?${params.toString().replace(/%2C/gi, ',')}`
          const cacheKey = `user-embed:${url}`

          let rows
          if (fetchedEndpoints[cacheKey]) {
            rows = fetchedEndpoints[cacheKey]
          } else {
            const res = await fetch(url)
            const data = await res.json()
            rows = Array.isArray(data) ? data : []
            fetchedEndpoints[cacheKey] = rows
          }

          const seen = new Set()
          const options = []
          rows.forEach(row => {
            if (!row || typeof row !== 'object') return
            // Support both qualified ('users.id') and short ('id') column names.
            const pick = short => {
              const qualified = `users.${short}`
              return row[qualified] !== undefined ? row[qualified] : row[short]
            }
            const rawId = pick('id')
            if (rawId === null || rawId === undefined || rawId === '') return
            // Store as string so it matches parseFilterValue output (comma-split strings),
            // otherwise vue-select can't reverse-lookup the option and falls back to showing the id.
            const id = String(rawId)
            if (seen.has(id)) return
            seen.add(id)
            const parts = ['user', 'name', 'first_name', 'second_name']
              .map((k, i) => {
                const v = pick(k)
                const s = v === null || v === undefined ? '' : String(v).trim()
                return i === 0 && s ? `(${s})` : s
              })
              .filter(Boolean)
            const label = parts.length > 0 ? parts.join(' ') : id
            options.push({ value: id, label })
          })

          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], options, loading: false })
          }
          pruneOrphanSelection(key, options)
        } catch (e) {
          console.warn(`[ChartFilterOptions] Failed to fetch user-embed options for ${key}:`, e)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], loading: false })
          }
        }
      } else if (config.type === 'json-keys') {
        try {
          const base = baseUrl?.value || ''
          const tk = token?.value || ''
          const params = new URLSearchParams()
          params.set('embed', 'true')
          params.set('theme', 'dark')
          params.set('dataset', config.dataset)
          params.set('view', 'json')
          params.set('columns', config.column)
          params.set('limit', String(config.limit || -1))
          if (!getDatasetModule(config.dataset)?.omitTimeRange) {
            params.set('from', resolveTimeBound(activeFilters?.value?.from || getDatasetDefaultFrom(config.dataset)))
            const currentTo = activeFilters?.value?.to
            if (currentTo) params.set('to', resolveTimeBound(currentTo, { end: true }))
          }
          if (tk) params.set('token', tk)
          params.set('plaintext', 'true')

          // Merge static where from config with cascade where from active filters
          const cascadeWhere = buildCascadeWhere(dataset, key)
          let mergedWhere = {}
          if (config.where) {
            mergedWhere = { ...config.where }
          }
          if (cascadeWhere) {
            try {
              const cascadeObj = JSON.parse(cascadeWhere)
              mergedWhere = { ...mergedWhere, ...cascadeObj }
            } catch { /* ignore parse errors */ }
          }
          if (Object.keys(mergedWhere).length > 0) {
            params.set('where', JSON.stringify(mergedWhere))
          }

          const url = `${base}/?${params.toString()}`
          const cacheKey = `json-keys:${url}`

          let rows
          if (fetchedEndpoints[cacheKey]) {
            rows = fetchedEndpoints[cacheKey]
          } else {
            const res = await fetch(url)
            rows = await res.json()
            if (Array.isArray(rows)) fetchedEndpoints[cacheKey] = rows
          }

          if (!Array.isArray(rows)) {
            if (filterOptionsMap[key]) {
              vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], loading: false })
            }
            continue
          }

          const questionsMap = {}
          const questionsAnswerCounts = {}
          const colKey = config.column
          const shortKey = config.column.split('.').pop()
          rows.forEach(row => {
            let val = row[colKey] !== undefined ? row[colKey] : row[shortKey]
            if (!val) return
            if (typeof val === 'string') {
              try { val = JSON.parse(val) } catch { return }
            }
            if (typeof val !== 'object' || Array.isArray(val) || val === null) return
            Object.entries(val).forEach(([question, answer]) => {
              if (Array.isArray(answer)) return
              if (!question || question.endsWith(' - comment') || question.endsWith(' - Comment')) return
              if (!questionsMap[question]) questionsMap[question] = new Set()
              if (!questionsAnswerCounts[question]) questionsAnswerCounts[question] = {}
              const answerStr = (answer !== null && answer !== undefined && answer !== '') ? String(answer) : null
              if (answerStr !== null) {
                questionsMap[question].add(answerStr)
                questionsAnswerCounts[question][answerStr] = (questionsAnswerCounts[question][answerStr] || 0) + 1
              }
            })
          })

          const options = Object.keys(questionsMap).map(q => ({ value: q, label: q }))
          const questionsMapArrays = {}
          Object.entries(questionsMap).forEach(([q, set]) => {
            questionsMapArrays[q] = [...set]
          })

          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, {
              ...filterOptionsMap[key],
              options,
              questionsMap: questionsMapArrays,
              questionsAnswerCounts,
              loading: false,
            })
          }
        } catch (e) {
          console.warn(`[ChartFilterOptions] Failed to fetch json-keys for ${key}:`, e)
          if (filterOptionsMap[key]) {
            vueSet(filterOptionsMap, key, { ...filterOptionsMap[key], loading: false })
          }
        }
      }
    }
  }

  const activePFLabel = computed(() => activeProcessFormLabel?.value || '')

  // Structural changes (dataset / visible filter set / questionnaire): clear cache + reload.
  watch([selectedDataset, filterKeys, activePFLabel], () => {
    Object.keys(fetchedEndpoints).forEach(k => delete fetchedEndpoints[k])
    loadFilterOptions()
  }, { immediate: true })

  // Filter-value changes: reload so embed options reflect the already-filtered dataset.
  // URL-based cache keys handle deduplication; debounce avoids bursts on quick toggles.
  let reloadTimer = null
  watch(() => activeFilters?.value, () => {
    clearTimeout(reloadTimer)
    reloadTimer = setTimeout(() => loadFilterOptions({ preserveOptions: true }), 250)
  }, { deep: true })

  const getOptionsForFilter = (filterKey) => {
    const entry = filterOptionsMap[filterKey]
    if (!entry) return []
    // Filter out any options with null/undefined value or label to avoid vue-select crashes
    return (entry.options || []).filter(opt => opt != null && opt.value != null && opt.label != null)
  }

  return {
    filterOptionsMap,
    getOptionsForFilter,
  }
}
